import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Table from "components/DesignSystem/Table/V2/Table";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import {
  TransactionTableBody,
  TransactionTableHandler,
} from "./TransactionsTable";
import { useEffect, useState } from "react";
import { Header, Row, flexRender, useReactTable } from "react-table-8.10.7";
import {
  Transactions as TransactionsType,
  TxnAccountType,
} from "types/Models/books";
import { TRANSACTION_COLUMNS } from "constants/transaction";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { pluralize } from "utils/pluralize";
import { VendorGroup } from "./utils/groupByVendor";
import type { TransactionTableColumns } from "./TransactionsTable";
import { CaretRight } from "components/icons/CaretRight";
import { useWindowSize } from "hooks/useWindowSize";

type SortField = "date" | "amount";
type SortDir = "asc" | "desc";
type GroupSortState = { field: SortField; dir: SortDir };

type Props = {
  vendorGroups: VendorGroup[];
  table: ReturnType<typeof useReactTable<TransactionsType>>;
  onRowClick: (
    uuid: string,
    e: Event | React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void;
  isRowsSelected: boolean;
  actionIconColumn: string[];
  firstPinnedColumn: string | undefined;
  animateTransactions: string[];
  onAnimateComplete: () => void;
  updateFilter: <S extends keyof TransactionTableColumns>(
    name: S,
    newValue: TransactionTableColumns[S]
  ) => void;
  values: TransactionTableColumns;
  txnAccountType?: TxnAccountType;
};

const DEFAULT_SORT: GroupSortState = { field: "date", dir: "desc" };

const getSortedRows = (
  group: VendorGroup,
  allRows: Row<TransactionsType>[],
  sort: GroupSortState
): Row<TransactionsType>[] => {
  const groupUuids = new Set(group.transactions.map((t) => t.transaction.uuid));
  const rows = allRows.filter((r) =>
    groupUuids.has(r.original.transaction.uuid)
  );

  return [...rows].sort((a, b) => {
    const aVal =
      sort.field === "date"
        ? a.original.transaction.date
        : a.original.transaction.amount;
    const bVal =
      sort.field === "date"
        ? b.original.transaction.date
        : b.original.transaction.amount;

    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sort.dir === "asc" ? cmp : -cmp;
  });
};

const getHeaderContent = (
  header: Header<TransactionsType, unknown>,
  sort: GroupSortState,
  onToggleSort: (field: SortField) => void
): React.ReactNode => {
  const columnId = header.column.id;

  if (columnId === TRANSACTION_COLUMNS.SELECT) {
    return null;
  }

  if (columnId === TRANSACTION_COLUMNS.DATE) {
    const isSorted = sort.field === "date" ? sort.dir : false;
    return (
      <HeadCellContent
        title="Date"
        sort={{
          nextSortOrder: sort.dir === "desc" ? "asc" : "desc",
          isSorted,
          sortingHandler: () => onToggleSort("date"),
        }}
      />
    );
  }

  if (columnId === TRANSACTION_COLUMNS.AMOUNT) {
    const isSorted = sort.field === "amount" ? sort.dir : false;
    return (
      <HeadCellContent
        title="Amount"
        align="right"
        sort={{
          nextSortOrder: sort.dir === "desc" ? "asc" : "desc",
          isSorted,
          sortingHandler: () => onToggleSort("amount"),
        }}
      />
    );
  }

  if (columnId === TRANSACTION_COLUMNS.VENDOR) {
    return <HeadCellContent title="To/From" />;
  }

  if (header.isPlaceholder) {
    return null;
  }

  return flexRender(header.column.columnDef.header, header.getContext());
};

export const TransactionVendorGroupAccordion = ({
  vendorGroups,
  table,
  onRowClick,
  isRowsSelected,
  actionIconColumn,
  firstPinnedColumn,
  animateTransactions,
  onAnimateComplete,
  updateFilter,
  values,
  txnAccountType,
}: Props) => {
  const { isMobile } = useWindowSize();

  const columnHandler = (
    <TransactionTableHandler
      updateFilter={updateFilter}
      values={values}
      txnAccountType={txnAccountType}
    />
  );
  const [openGroups, setOpenGroups] = useState<string[]>(
    vendorGroups[0] ? [vendorGroups[0].vendorKey] : []
  );

  const vendorKeys = vendorGroups.map((g) => g.vendorKey);

  useEffect(() => {
    setOpenGroups(vendorGroups[0] ? [vendorGroups[0].vendorKey] : []);
  }, [JSON.stringify(vendorKeys)]);

  const [groupSorts, setGroupSorts] = useState<Map<string, GroupSortState>>(
    new Map()
  );

  const toggleSort = (vendorKey: string, field: SortField) => {
    setGroupSorts((prev) => {
      const next = new Map(prev);
      const current = prev.get(vendorKey) ?? DEFAULT_SORT;
      const dir =
        current.field === field && current.dir === "desc" ? "asc" : "desc";
      next.set(vendorKey, { field, dir });
      return next;
    });
  };

  const allRows = table.getRowModel().rows;

  return (
    <Accordion.Root
      type="multiple"
      value={openGroups}
      onValueChange={setOpenGroups}
      className="t-flex t-flex-col"
    >
      {vendorGroups.map((group) => {
        const sort = groupSorts.get(group.vendorKey) ?? DEFAULT_SORT;
        const sortedRows = getSortedRows(group, allRows, sort);

        const allGroupSelected =
          sortedRows.length > 0 && sortedRows.every((r) => r.getIsSelected());
        const someGroupSelected = sortedRows.some((r) => r.getIsSelected());

        // On phones the count sits under the vendor name instead of beside the amount.
        const transactionCount = (
          <span className="t-text-body-sm-italic t-italic t-text-text-30">
            {pluralize(
              group.transactions.length,
              "transaction",
              "transactions"
            )}
          </span>
        );

        return (
          <Accordion.RawItem key={group.vendorKey} value={group.vendorKey}>
            <div className="t-w-full t-flex t-items-center t-pr-4 hover:t-bg-surface-lighter-grey t-border-b t-border-0 t-border-solid t-border-neutral-0">
              <Checkbox
                checked={allGroupSelected}
                indeterminate={someGroupSelected && !allGroupSelected}
                onChange={(e) =>
                  sortedRows.forEach((r) => r.toggleSelected(e.target.checked))
                }
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select all transactions for ${group.vendorName}`}
                className="t-ml-1.5"
              />
              <Accordion.RawTrigger asChild>
                <button className="all:unset t-group t-flex-1 t-flex t-items-center t-justify-between t-py-3 t-pl-3 t-cursor-pointer t-select-none focus-visible:t-outline  t-outline-purple-20   ">
                  <div className="t-flex t-items-center t-gap-3">
                    <span className="t-text-text-30 t-transition-transform t-duration-200 group-data-state-open:t-rotate-90 t-flex t-items-center">
                      <CaretRight strokeWidth="1.6" className="t-size-3" />
                    </span>
                    <div className="t-flex t-flex-col t-items-start">
                      <span className="t-text-body-sm t-text-text-100">
                        {group.vendorName}
                      </span>
                      {isMobile && transactionCount}
                    </div>
                  </div>
                  <div className="t-flex t-items-center t-gap-4 t-ml-auto">
                    {!isMobile && (
                      <>
                        {transactionCount}
                        <span className="t-w-px t-h-4 t-bg-neutral-10 t-block" />
                      </>
                    )}

                    <div className="t-text-body-sm t-text-text-60">
                      <AmountSuperScript amount={group.totalAmount} />
                    </div>
                  </div>
                </button>
              </Accordion.RawTrigger>
            </div>

            <Accordion.RawContent className="data-[state=open]:t-animate-accordion-down data-[state=closed]:t-animate-accordion-up t-overflow-hidden">
              <div className="t-relative">
                {!isMobile && (
                  <div className="t-absolute t-top-0 t-right-0 t-z-filter-head t-pt-1 t-mr-1">
                    {columnHandler}
                  </div>
                )}
                <Table.Container className="t-w-full t-overflow-auto">
                  <Table.Content>
                    <Table.Head>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Table.HeadRow key={headerGroup.id}>
                          {headerGroup.headers.map((header, index) => {
                            const styles = getCommonPinningStyles(
                              header.column
                            );
                            const isPinnedColumn = header.column.getIsPinned();
                            const isLastHeader =
                              index === headerGroup.headers.length - 1;

                            const headerContent = getHeaderContent(
                              header,
                              sort,
                              (field) => toggleSort(group.vendorKey, field)
                            );

                            return (
                              <Table.HeadCell
                                key={header.id}
                                className={classNames(
                                  "t-text-subtext-sm t-group",
                                  {
                                    "!t-bg-surface-lighter-grey":
                                      isPinnedColumn,
                                    "t-drop-shadow-table-left-pin":
                                      firstPinnedColumn === header.id,
                                  }
                                )}
                                size={
                                  actionIconColumn.includes(header.id)
                                    ? "small"
                                    : "regular"
                                }
                                style={{
                                  minWidth: header.getSize(),
                                  ...styles,
                                }}
                                isUpperCase={false}
                              >
                                <span className="t-flex t-gap-1 t-items-center">
                                  {headerContent}
                                  {isMobile && isLastHeader && columnHandler}
                                </span>
                              </Table.HeadCell>
                            );
                          })}
                        </Table.HeadRow>
                      ))}
                    </Table.Head>

                    <TransactionTableBody
                      rows={sortedRows}
                      onRowClick={onRowClick}
                      isRowsSelected={isRowsSelected}
                      actionIconColumn={actionIconColumn}
                      firstPinnedColumn={firstPinnedColumn}
                      animateTransactions={animateTransactions}
                      onAnimateComplete={onAnimateComplete}
                    />
                  </Table.Content>
                </Table.Container>
              </div>
            </Accordion.RawContent>
          </Accordion.RawItem>
        );
      })}
    </Accordion.Root>
  );
};
